import {
    type CSSProperties,
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    Progress,
    Button,
    Icon,
    Card,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Image as ImageIcon
} from "lucide-react-native";
import {
    type DocsStyleTokens,
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Card";

const USAGE_CODE = `<Card
    isLoading={isRefreshing}
>
    <Card.Header>
        <Card.Description>
            Active users
        </Card.Description>
        <Card.Title
            isMetric={true}
        >
            1,284
        </Card.Title>
        <Card.Action>
            <Button
                onPress={handleRefresh}
                variant="outline"
                title="Refresh"
                type="neutral"
                size="small"
            />
        </Card.Action>
    </Card.Header>
    <Card.Content>
        <Text>
            +12% in the last 30 days
        </Text>
    </Card.Content>
</Card>`;

const COMPOSITION_CODE = `<Card>
    <Card.Content
        isFlush={true}
    >
        <Cover />
    </Card.Content>
    <Card.Header>
        <Card.Title>
            Spring campaign
        </Card.Title>
        <Card.Description>
            Draft · 3 contributors
        </Card.Description>
    </Card.Header>
    <Card.Footer
        isDivided={false}
        align="stretch"
    >
        <Button
            variant="outline"
            onPress={handlePreview}
            title="Preview"
            type="neutral"
        />
        <Button
            onPress={handlePublish}
            title="Publish"
        />
    </Card.Footer>
</Card>`;

const BAND_CODE = `<Card.Footer
    variant="band"
    align="between"
>
    <Text>
        Next invoice Nov 1
    </Text>
    <Button
        title="Upgrade"
        size="small"
    />
</Card.Footer>`;

const BAND_PROGRESS = 60;

const BAND_VARIANTS = [
    "outline",
    "filled"
] as const;

const bandStyles = StyleSheet.create({
    row: {
        flexDirection: "row"
    },
    actions: {
        flexDirection: "row"
    },
    cell: {
        flex: 1
    }
});

const VARIANTS = [
    "outline",
    "elevated",
    "filled",
    "inset"
] as const;

const SIZES = [
    "medium",
    "small"
] as const;

const INITIAL_USERS = 1284;

const USERS_STEP = 7;

const REFRESH_DELAY = 1200;

const getMediaStyle = ({
    colors
}: DocsStyleTokens): CSSProperties => ({
    background: colors.content.container.subtle,
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    width: "100%",
    height: 160
});

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.variants"),
        id: "variants"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("cardPage.compositionTitle"),
        id: "composition"
    },
    {
        title: t("cardPage.bandTitle"),
        id: "band"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const CardPage = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        language,
        t
    } = useDocsI18n();

    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width: windowWidth
    } = useWindowDimensions();

    const isNarrow = windowWidth < shapeConfigs.compactBreakpoint;

    const [
        users,
        setUsers
    ] = useState(INITIAL_USERS);

    const [
        isRefreshing,
        setIsRefreshing
    ] = useState(false);

    const [
        openCount,
        setOpenCount
    ] = useState(0);

    useEffect(() => {
        if(!isRefreshing) {
            return;
        }

        const timer = window.setTimeout(() => {
            setUsers((current) => current + USERS_STEP);

            setIsRefreshing(false);
        }, REFRESH_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isRefreshing
    ]);

    const handleRefresh = () => setIsRefreshing(true);

    const handleOpen = () => setOpenCount(openCount + 1);

    return <Article>
        <ComponentHeader
            lead={t("cardPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("cardPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("cardPage.overviewNote")}
            </Paragraph>
            <Preview
                caption={t("cardPage.overviewCaption")}
                isStacked={true}
            >
                <Card
                    isLoading={isRefreshing}
                >
                    <Card.Header>
                        <Card.Description>
                            {t("sample.metric")}
                        </Card.Description>
                        <Card.Title
                            isMetric={true}
                        >
                            {users.toLocaleString(language)}
                        </Card.Title>
                        <Card.Action>
                            <Button
                                title={t("cardPage.refresh")}
                                onPress={handleRefresh}
                                variant="outline"
                                type="neutral"
                                size="small"
                            />
                        </Card.Action>
                    </Card.Header>
                    <Card.Content>
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("cardPage.metricNote")}
                        </Text>
                    </Card.Content>
                </Card>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.variants")}
            id="variants"
        >
            <Paragraph>
                {t("cardPage.variantsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    VARIANTS.map((variant) => <Card
                        variant={variant}
                        key={variant}
                    >
                        <Card.Header>
                            <Card.Title>
                                {`variant="${variant}"`}
                            </Card.Title>
                            <Card.Description>
                                {t("sample.body")}
                            </Card.Description>
                        </Card.Header>
                    </Card>)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("cardPage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => <Card
                        size={size}
                        key={size}
                    >
                        <Card.Header>
                            <Card.Title>
                                {`size="${size}"`}
                            </Card.Title>
                            <Card.Description>
                                {t("sample.body")}
                            </Card.Description>
                        </Card.Header>
                        <Card.Footer
                            isDivided={false}
                        >
                            <Button
                                title={t("sample.action")}
                                onPress={() => undefined}
                                size={size}
                            />
                        </Card.Footer>
                    </Card>)
                }
            </Preview>
        </Section>
        <Section
            title={t("cardPage.compositionTitle")}
            id="composition"
        >
            <Paragraph>
                {t("cardPage.compositionText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Card>
                    <Card.Header
                        isDivided={true}
                    >
                        <Card.Title>
                            {t("cardPage.reportTitle")}
                        </Card.Title>
                        <Card.Description>
                            {t("cardPage.reportDescription")}
                        </Card.Description>
                        <Card.Action>
                            <Button
                                title={t("cardPage.export")}
                                onPress={() => undefined}
                                variant="outline"
                                type="neutral"
                                size="small"
                            />
                        </Card.Action>
                    </Card.Header>
                    <Card.Content>
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("cardPage.reportBody")}
                        </Text>
                    </Card.Content>
                    <Card.Footer
                        align="between"
                    >
                        <Text
                            variant="bodySmallSize"
                            color="low"
                        >
                            {t("cardPage.updated")}
                        </Text>
                        <Button
                            title={t("cardPage.openReport")}
                            onPress={() => undefined}
                        />
                    </Card.Footer>
                </Card>
                <Card>
                    <Card.Content
                        isFlush={true}
                    >
                        <div
                            aria-label={t("cardPage.coverLabel")}
                            style={getMediaStyle(docsTokens)}
                            role="img"
                        >
                            <Icon
                                icon={ImageIcon}
                                size="large"
                                color="low"
                            />
                        </div>
                    </Card.Content>
                    <Card.Header>
                        <Card.Title>
                            {t("cardPage.campaignTitle")}
                        </Card.Title>
                        <Card.Description>
                            {t("cardPage.campaignDescription")}
                        </Card.Description>
                    </Card.Header>
                    <Card.Footer
                        isDivided={false}
                        align="stretch"
                    >
                        <Button
                            title={t("cardPage.preview")}
                            onPress={() => undefined}
                            variant="outline"
                            type="neutral"
                        />
                        <Button
                            title={t("cardPage.publish")}
                            onPress={() => undefined}
                        />
                    </Card.Footer>
                </Card>
            </Preview>
            <CodeBlock
                code={COMPOSITION_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("cardPage.bandTitle")}
            id="band"
        >
            <Paragraph>
                {t("cardPage.bandText")}
            </Paragraph>
            <Preview
                caption={t("cardPage.bandCaption")}
            >
                <View
                    style={[
                        bandStyles.row,
                        {
                            flexDirection: isNarrow ? "column" : "row",
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    {
                        BAND_VARIANTS.map((variant) => <View
                            style={isNarrow ? undefined : bandStyles.cell}
                            key={variant}
                        >
                            <Card
                                spreadBehaviour="stretch"
                                variant={variant}
                            >
                                <Card.Header>
                                    <Card.Title>
                                        {t("cardPage.bandCardTitle")}
                                    </Card.Title>
                                    <Card.Description>
                                        {t("cardPage.bandCardDescription")}
                                    </Card.Description>
                                </Card.Header>
                                <Card.Content>
                                    <Progress
                                        aria-label={t("cardPage.bandCardTitle")}
                                        value={BAND_PROGRESS}
                                    />
                                </Card.Content>
                                <Card.Footer
                                    align="between"
                                    variant="band"
                                >
                                    <Text
                                        variant="bodySmallSize"
                                        color="low"
                                    >
                                        {t("cardPage.bandNextBill")}
                                    </Text>
                                    <View
                                        style={[
                                            bandStyles.actions,
                                            {
                                                gap: spaces.spacingSm
                                            }
                                        ]}
                                    >
                                        <Button
                                            title={t("cardPage.bandManage")}
                                            onPress={() => undefined}
                                            variant="outline"
                                            type="neutral"
                                            size="small"
                                        />
                                        <Button
                                            title={t("cardPage.bandUpgrade")}
                                            onPress={() => undefined}
                                            size="small"
                                        />
                                    </View>
                                </Card.Footer>
                            </Card>
                        </View>)
                    }
                </View>
            </Preview>
            <CodeBlock
                code={BAND_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("cardPage.statesText")}
            </Paragraph>
            <Preview
                caption={t("cardPage.statesCaption")}
                isStacked={true}
            >
                <Card
                    onPress={handleOpen}
                    isPressable={true}
                    size="small"
                >
                    <Card.Header>
                        <Card.Title>
                            {t("cardPage.projectTitle")}
                        </Card.Title>
                        <Card.Description>
                            {t("cardPage.openedCount", [
                                openCount
                            ])}
                        </Card.Description>
                    </Card.Header>
                </Card>
                <Card
                    onPress={() => undefined}
                    isPressable={true}
                    isDisabled={true}
                    size="small"
                >
                    <Card.Header>
                        <Card.Title>
                            {t("cardPage.archivedTitle")}
                        </Card.Title>
                        <Card.Description>
                            {t("cardPage.archivedDescription")}
                        </Card.Description>
                    </Card.Header>
                </Card>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("cardPage.a11yPlain"),
                    t("cardPage.a11yHeading"),
                    t("cardPage.a11yPress"),
                    t("cardPage.a11yName"),
                    t("cardPage.a11yState"),
                    t("cardPage.a11yWarn")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("cardPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        type: "\"outline\" | \"elevated\" | \"filled\" | \"inset\"",
                        description: t("cardPage.propVariant"),
                        defaultValue: "\"outline\"",
                        name: "variant"
                    },
                    {
                        description: t("cardPage.propSize"),
                        type: "\"small\" | \"medium\"",
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("cardPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("cardPage.propIsPressable"),
                        defaultValue: "false",
                        name: "isPressable",
                        type: "boolean"
                    },
                    {
                        description: t("cardPage.propOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("cardPage.propHref"),
                        type: "string",
                        name: "href"
                    },
                    {
                        description: t("cardPage.propHrefAttrs"),
                        type: "{ download, target, rel }",
                        name: "hrefAttrs"
                    },
                    {
                        description: t("cardPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("cardPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("cardPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("cardPage.propRole"),
                        type: "string",
                        name: "role"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("cardPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("cardPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("cardPage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("cardPage.partsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("cardPage.propPartVariant"),
                        type: "\"plain\" | \"band\"",
                        defaultValue: "\"plain\"",
                        name: "variant"
                    },
                    {
                        description: t("cardPage.propPartIsDivided"),
                        name: "isDivided",
                        type: "boolean"
                    },
                    {
                        description: t("cardPage.propPartHeadingLevel"),
                        name: "headingLevel",
                        defaultValue: "3",
                        type: "2 | 3 | 4"
                    },
                    {
                        description: t("cardPage.propPartIsMetric"),
                        defaultValue: "false",
                        name: "isMetric",
                        type: "boolean"
                    },
                    {
                        description: t("cardPage.propPartIsFlush"),
                        defaultValue: "false",
                        name: "isFlush",
                        type: "boolean"
                    },
                    {
                        type: "\"start\" | \"end\" | \"between\" | \"stretch\"",
                        description: t("cardPage.propPartAlign"),
                        defaultValue: "\"start\"",
                        name: "align"
                    },
                    {
                        description: t("cardPage.propPartPlaceholder"),
                        name: "placeholder",
                        type: "ReactNode"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default CardPage;
